<template>
  <div class="relative">
    <!-- Mobile Full-Screen Overlay -->
    <div v-if="showMobileOverlay" class="fixed inset-0 z-50 bg-white md:hidden">
      <!-- Top Search Bar -->
      <div class="flex items-center p-4 border-b border-gray-200">
        <div class="flex-1 relative">
          <input
            ref="mobileSearchInput"
            v-model="searchQuery"
            type="text"
            placeholder="Search shows..."
            class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all duration-200 bg-gray-100 text-black"
            @input="handleSearch"
            @keyup.escape="closeMobileOverlay"
          />
          <svg
            class="absolute ml-3 top-0 mt-3 h-5 w-5 text-gray-400 pointer-events-none"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <button
          @click="closeMobileOverlay"
          class="ml-4 text-orange-500 font-medium text-base"
        >
          Cancel
        </button>
      </div>

      <!-- Search Results -->
      <div class="flex-1 overflow-y-auto">
        <!-- No results state -->
        <div v-if="filteredEvents.length === 0 && searchQuery.length > 0" class="p-6 text-center text-gray-500">
          <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
          <div class="text-sm font-medium text-gray-700 mb-1">No shows found</div>
          <div class="text-xs text-gray-500">Try a different search term</div>
        </div>

        <!-- Search results -->
        <div v-if="filteredEvents.length > 0" class="divide-y divide-gray-100">
          <div
            v-for="event in filteredEvents"
            :key="event.id || event.event_identifier"
            @click="selectEventMobile(event)"
            class="flex items-center py-4 px-4 hover:bg-gray-50 cursor-pointer"
          >
            <!-- Event Cover Photo -->
            <div class="flex-shrink-0 w-12 h-12 mr-4 rounded-lg overflow-hidden bg-gray-200">
              <img
                :src="getEventCoverPhoto(event)"
                :alt="`${event.name} cover photo`"
                class="w-full h-full object-cover"
                @error="handleImageError"
              />
            </div>

            <!-- Content -->
            <div class="flex-1 min-w-0">
              <div class="font-semibold text-gray-900 text-base mb-1">
                {{ event.name }}
              </div>
              <div class="flex items-center space-x-2 text-sm text-gray-500">
                <span v-if="event.start_date">{{ formatDate(event.start_date) }}</span>
                <span v-if="event.start_date && event.location_details">•</span>
                <span v-if="event.location_details">
                  {{ event.location_details.city }}, {{ event.location_details.state }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Explore all Shows option -->
        <div v-if="!isOnShowsPage && searchQuery" class="border-t border-gray-100 mt-4">
          <div
            @click="exploreAllShowsMobile"
            class="flex items-center py-4 px-4 hover:bg-blue-50 cursor-pointer transition-colors"
          >
            <div class="flex-shrink-0 w-12 h-12 mr-4 flex items-center justify-center rounded-lg bg-blue-100">
              <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
              </svg>
            </div>
            <div class="flex-1">
              <div class="font-semibold text-blue-700 text-base">
                Explore all Shows
              </div>
              <div class="text-sm text-blue-600 mt-1">
                Browse all upcoming livestock shows
              </div>
            </div>
            <div class="flex-shrink-0">
              <svg class="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </div>
          </div>
        </div>

        <!-- Empty state when no search query -->
        <div v-if="searchQuery.length === 0" class="p-6 text-center text-gray-500">
          <svg class="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
          <div class="text-lg font-medium text-gray-700 mb-2">Search by location</div>
          <div class="text-sm text-gray-500">Search by city, show name, or state</div>
        </div>
      </div>
    </div>

    <!-- Desktop/Default Search Bar -->
    <div class="relative" :class="{ 'md:block hidden': showMobileOverlay }">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search shows..."
        class="w-full pl-8 md:pl-10 pr-4 py-1.5 md:py-3 border-2 text-black border-orange-200_ rounded-xl md:rounded-full focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all duration-200 bg-gray-200 text-base"
        @input="handleSearch"
        @focus="handleFocus"
        @blur="handleBlur"
      />
      <svg
        class="absolute left-0 top-0 mt-3 ml-2 md:ml-3 h-4 w-4 md:h-5 md:w-5 text-orange-400 pointer-events-none"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    </div>

    <!-- Desktop Dropdown -->
    <div
      v-if="showDropdown && !isMobile && (filteredEvents.length > 0 || searchQuery.length > 0 || (!isOnShowsPage && searchQuery.length === 0))"
      class="absolute z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden w-full left-0"
    >
      <!-- No results state -->
      <div v-if="filteredEvents.length === 0 && searchQuery.length > 0" class="p-6 text-center text-gray-500">
        <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
        </svg>
        <div class="text-sm font-medium text-gray-700 mb-1">No shows found</div>
        <div class="text-xs text-gray-500">Try a different search term</div>
      </div>

      <!-- Search results -->
      <div v-if="filteredEvents.length > 0" class="max-h-80 overflow-y-auto">
        <div
          v-for="event in filteredEvents"
          :key="event.id || event.event_identifier"
          @mousedown="selectEvent(event)"
          class="flex items-center py-3 px-4 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
        >
          <!-- Event Cover Photo instead of State Abbreviation -->
          <div class="flex-shrink-0 w-10 h-10 mr-4 rounded-lg overflow-hidden bg-gray-200">
            <img
              :src="getEventCoverPhoto(event)"
              :alt="`${event.name} cover photo`"
              class="w-full h-full object-cover"
              @error="handleImageError"
            />
          </div>

          <!-- Content -->
          <div class="flex-1 min-w-0 mr-4">
            <div class="font-semibold text-gray-900 truncate text-base">
              {{ event.name }}
            </div>
            <div class="flex space-x-1.5 items-center">
              <div v-if="event.start_date" class="text-sm text-gray-500 mt-1">
                {{ formatDate(event.start_date) }}
              </div>
              <div v-if="event.location_details" class="text-sm text-gray-600 truncate mt-1">
                {{ event.location_details.city }}, {{ event.location_details.state }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Explore all Shows option - always visible at bottom -->
      <div class="border-t border-gray-100" v-if="!isOnShowsPage && searchQuery">
        <div
          @mousedown="exploreAllShows"
          class="flex items-center py-3 px-4 hover:bg-blue-100 cursor-pointer transition-colors"
        >
          <div class="flex-shrink-0 w-10 h-10 mr-4 flex items-center justify-center rounded-lg bg-blue-100">
            <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
            </svg>
          </div>
          <div class="flex-1">
            <div class="font-semibold text-blue-700 text-base">
              Explore all Shows
            </div>
            <div class="text-sm text-blue-600 mt-1">
              Browse all upcoming livestock shows
            </div>
          </div>
          <div class="flex-shrink-0">
            <svg class="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ShowSearchBar',
  props: {
    events: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      searchQuery: '',
      showDropdown: false,
      filteredEvents: [],
      showMobileOverlay: false, // New state for mobile overlay
      isMobile: false // New state for mobile detection
    }
  },
  computed: {
    // Check if we're currently on the shows page (home route)
    isOnShowsPage() {
      return this.$route.name === 'home' || this.$route.path === '/'
    }
  },
  watch: {
    $route () {
      // Close mobile overlay when route changes
      this.closeMobileOverlay()
    }
  },
  mounted () {
    this.checkIsMobile()
    window.addEventListener('resize', this.checkIsMobile)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.checkIsMobile)
  },
  methods: {
    checkIsMobile () {
      this.isMobile = window.innerWidth < 768 // md breakpoint
    },
    handleSearch () {
      if (this.searchQuery.length === 0) {
        this.filteredEvents = []
        return
      }

      const query = this.searchQuery.toLowerCase()

      this.filteredEvents = this.events.filter(event => {
        const nameMatch = event.name && event.name.toLowerCase().includes(query)
        const cityMatch = event.location_details && event.location_details.city &&
          event.location_details.city.toLowerCase().includes(query)

        // Check both full state name and abbreviation
        const stateMatch = this.matchesState(event, query)

        return nameMatch || cityMatch || stateMatch
      }).slice(0, 8) // Limit to 8 results for better UX
    },
    matchesState (event, query) {
      if (!event.location_details || !event.location_details.state) {
        return false
      }

      const stateOnEvent = event.location_details.state // This might be abbreviation or full name
      const queryLower = query.toLowerCase()

      // Check for exact match with what's stored on the event (could be abbreviation or full name)
      if (stateOnEvent.toLowerCase() === queryLower) {
        return true
      }

      // If what's stored is an abbreviation, get the full name and compare
      const fullStateName = this.getFullStateNameFromAbbr(stateOnEvent)
      if (fullStateName && fullStateName.toLowerCase() === queryLower) {
        return true
      }

      // If what's stored is a full name, get the abbreviation and compare
      const abbreviation = this.getKnownStateAbbreviation(stateOnEvent)
      if (abbreviation && abbreviation.toLowerCase() === queryLower) {
        return true
      }

      return false
    },
    getKnownStateAbbreviation (stateName) {
      const stateAbbreviations = {
        'alabama': 'AL',
        'alaska': 'AK',
        'arizona': 'AZ',
        'arkansas': 'AR',
        'california': 'CA',
        'colorado': 'CO',
        'connecticut': 'CT',
        'delaware': 'DE',
        'florida': 'FL',
        'georgia': 'GA',
        'hawaii': 'HI',
        'idaho': 'ID',
        'illinois': 'IL',
        'indiana': 'IN',
        'iowa': 'IA',
        'kansas': 'KS',
        'kentucky': 'KY',
        'louisiana': 'LA',
        'maine': 'ME',
        'maryland': 'MD',
        'massachusetts': 'MA',
        'michigan': 'MI',
        'minnesota': 'MN',
        'mississippi': 'MS',
        'missouri': 'MO',
        'montana': 'MT',
        'nebraska': 'NE',
        'nevada': 'NV',
        'new hampshire': 'NH',
        'new jersey': 'NJ',
        'new mexico': 'NM',
        'new york': 'NY',
        'north carolina': 'NC',
        'north dakota': 'ND',
        'ohio': 'OH',
        'oklahoma': 'OK',
        'oregon': 'OR',
        'pennsylvania': 'PA',
        'rhode island': 'RI',
        'south carolina': 'SC',
        'south dakota': 'SD',
        'tennessee': 'TN',
        'texas': 'TX',
        'utah': 'UT',
        'vermont': 'VT',
        'virginia': 'VA',
        'washington': 'WA',
        'west virginia': 'WV',
        'wisconsin': 'WI',
        'wyoming': 'WY',
        // Canadian provinces
        'ontario': 'ON',
        'british columbia': 'BC',
        'alberta': 'AB',
        'saskatchewan': 'SK',
        'manitoba': 'MB',
        'quebec': 'QC',
        'new brunswick': 'NB',
        'nova scotia': 'NS',
        'prince edward island': 'PE',
        'newfoundland and labrador': 'NL',
        'northwest territories': 'NT',
        'nunavut': 'NU',
        'yukon': 'YT'
      }

      return stateAbbreviations[stateName.toLowerCase()] || null
    },
    selectEvent (event) {
      this.searchQuery = event.name
      this.showDropdown = false
      this.$emit('select', event)
    },
    exploreAllShows () {
      this.searchQuery = ''
      this.showDropdown = false
      // Navigate to home page which shows all upcoming shows
      this.$router.push('/')
    },
    handleBlur () {
      // Delay hiding dropdown to allow click events to register
      setTimeout(() => {
        this.showDropdown = false
      }, 150)
    },
    formatDate (dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    },
    getEventCoverPhoto (event) {
      // Check for cover photo in event details
      if (event.event_details && event.event_details.event_card_cover_url) {
        return event.event_details.event_card_cover_url
      }

      // Fallback to generated default cover photo similar to other components
      if (event.name) {
        return `https://res.cloudinary.com/showman/image/upload/c_fill,g_auto,w_480,ar_16:7/c_scale,co_rgb:ffffff,l_text:montserrat_50_align_center_style_bold:${encodeURIComponent(event.name)},g_center,w_400,c_fit/v1620138098/defaultPhotos/Blank_Showman_Orange.png`
      }

      // Final fallback
      return 'https://res.cloudinary.com/showman/image/upload/v1620138098/defaultPhotos/Blank_Showman_Orange.png'
    },
    handleImageError (e) {
      // Fallback to a simple default image if the cover photo fails to load
      e.target.src = 'https://res.cloudinary.com/showman/image/upload/v1620138098/defaultPhotos/Blank_Showman_Orange.png'
    },
    getFullStateNameFromAbbr (abbreviation) {
      const abbrToFullName = {
        'AL': 'Alabama',
        'AK': 'Alaska',
        'AZ': 'Arizona',
        'AR': 'Arkansas',
        'CA': 'California',
        'CO': 'Colorado',
        'CT': 'Connecticut',
        'DE': 'Delaware',
        'FL': 'Florida',
        'GA': 'Georgia',
        'HI': 'Hawaii',
        'ID': 'Idaho',
        'IL': 'Illinois',
        'IN': 'Indiana',
        'IA': 'Iowa',
        'KS': 'Kansas',
        'KY': 'Kentucky',
        'LA': 'Louisiana',
        'ME': 'Maine',
        'MD': 'Maryland',
        'MA': 'Massachusetts',
        'MI': 'Michigan',
        'MN': 'Minnesota',
        'MS': 'Mississippi',
        'MO': 'Missouri',
        'MT': 'Montana',
        'NE': 'Nebraska',
        'NV': 'Nevada',
        'NH': 'New Hampshire',
        'NJ': 'New Jersey',
        'NM': 'New Mexico',
        'NY': 'New York',
        'NC': 'North Carolina',
        'ND': 'North Dakota',
        'OH': 'Ohio',
        'OK': 'Oklahoma',
        'OR': 'Oregon',
        'PA': 'Pennsylvania',
        'RI': 'Rhode Island',
        'SC': 'South Carolina',
        'SD': 'South Dakota',
        'TN': 'Tennessee',
        'TX': 'Texas',
        'UT': 'Utah',
        'VT': 'Vermont',
        'VA': 'Virginia',
        'WA': 'Washington',
        'WV': 'West Virginia',
        'WI': 'Wisconsin',
        'WY': 'Wyoming',
        // Canadian provinces
        'ON': 'Ontario',
        'BC': 'British Columbia',
        'AB': 'Alberta',
        'SK': 'Saskatchewan',
        'MB': 'Manitoba',
        'QC': 'Quebec',
        'NB': 'New Brunswick',
        'NS': 'Nova Scotia',
        'PE': 'Prince Edward Island',
        'NL': 'Newfoundland and Labrador',
        'NT': 'Northwest Territories',
        'NU': 'Nunavut',
        'YT': 'Yukon'
      }

      return abbrToFullName[abbreviation.toUpperCase()] || null
    },
    handleFocus () {
      // Open mobile overlay when search bar is focused on mobile
      if (this.isMobile) {
        this.showMobileOverlay = true
        this.$nextTick(() => {
          this.$refs.mobileSearchInput.focus()
        })
      } else {
        this.showDropdown = true
      }
    },
    selectEventMobile (event) {
      this.searchQuery = event.name
      this.showMobileOverlay = false
      this.$emit('select', event)
    },
    exploreAllShowsMobile () {
      this.searchQuery = ''
      this.showMobileOverlay = false
      // Navigate to home page which shows all upcoming shows
      this.$router.push('/')
    },
    closeMobileOverlay () {
      this.showMobileOverlay = false
      this.searchQuery = ''
      this.filteredEvents = []
    }
  }
}
</script>

<style scoped>
/* Ensure dropdown positioning */
.absolute {
  left: 0;
  right: auto;
}

/* Custom scrollbar for dropdown */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
